import { useCallback, useEffect, useRef, useState } from 'react';
import CampoPeriodo from '@/components/base/CampoPeriodo';
import { LIMITE_TEXTO, LIMITE_TEXTO_LONGO } from '@/lib/mascaras';
import { dataValida, MENSAGEM_DATA } from '@/lib/datas';
import { useJanelaDeAlerta, type ChaveDeAlerta } from '@/lib/notificacoes';
import { validarPeriodo, type PeriodoParcial } from '@/lib/periodo';
import {
  anexarArquivo,
  CLASSE_SITUACAO_DOCUMENTO,
  gravarDocumento,
  listarDocumentos,
  removerDocumentoLogicamente,
  ROTULO_SITUACAO_DOCUMENTO,
  situacaoDoDocumento,
  urlDoArquivo,
  type Documento,
  type DonoDoDocumento,
  type TipoDeDocumento,
} from '@/lib/documentosRepo';

// Bloco de documentos (componente 3.3 da especificação).
//
// Construção única, como o bloco de endereço: o item de infraestrutura, o
// alvará e a ficha do funcionário usam este mesmo componente. Os três pedem a
// mesma coisa — nome, número, emissão, validade, arquivo — e escrever três
// versões faria as três divergirem na primeira correção.
//
// ─── Validade: data ou Período, nunca os dois ────────────────────────
//
// A especificação admite as duas formas, e a constraint do banco recusa as duas
// juntas: seriam dois vencimentos para o mesmo papel. O formulário faz a escolha
// explícita, em vez de deixar a pessoa preencher os dois e descobrir no erro.

interface Props {
  dono: DonoDoDocumento;
  /** Condomínio do dono — compõe o caminho no bucket privado. */
  condominioId: string;
  titulo?: string;
  /** Tipos que este ponto de uso aceita. O primeiro é o padrão do formulário. */
  tipos?: TipoDeDocumento[];
  /** Qual janela de alerta classifica "a vencer" aqui. */
  chaveDeAlerta?: ChaveDeAlerta;
  /** Só leitura: sem permissão de cadastros, o banco recusaria a gravação. */
  somenteLeitura?: boolean;
  /** Fixado no registro profissional, que é sempre de uma profissão. */
  profissaoId?: string | null;
  onMudou?: () => void;
}

const CLASSE =
  'w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 disabled:opacity-60';

const ROTULO_TIPO: Record<TipoDeDocumento, string> = {
  treinamento: 'Certificado de treinamento',
  alvara: 'Alvará / licença',
  registro_profissional: 'Registro profissional',
  avulso: 'Documento avulso',
};

const formatData = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

interface Formulario {
  id?: string;
  tipo: TipoDeDocumento;
  nome: string;
  numero: string;
  descricao: string;
  emitidoEm: string;
  /** 'data' ou 'periodo' — a escolha que a constraint torna exclusiva. */
  modoValidade: 'data' | 'periodo';
  validade: string;
  validadePeriodo: PeriodoParcial;
}

const formularioVazio = (tipo: TipoDeDocumento): Formulario => ({
  tipo,
  nome: '',
  numero: '',
  descricao: '',
  emitidoEm: '',
  modoValidade: 'data',
  validade: '',
  validadePeriodo: { valor: null, unidade: null },
});

export default function DocumentosDoRegistro({
  dono,
  condominioId,
  titulo = 'Documentos',
  tipos = ['avulso'],
  chaveDeAlerta = 'alvara',
  somenteLeitura = false,
  profissaoId = null,
  onMudou,
}: Props) {
  const diasDeAlerta = useJanelaDeAlerta(chaveDeAlerta);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [form, setForm] = useState<Formulario | null>(null);
  const [salvando, setSalvando] = useState(false);
  const arquivoRef = useRef<HTMLInputElement | null>(null);
  const [arquivoEscolhido, setArquivoEscolhido] = useState<File | null>(null);

  // O dono vem como objeto literal do componente pai, então uma nova referência
  // a cada render. Reagir a ela recarregaria a lista em loop; reagir ao tipo e
  // ao id — que é o que de fato identifica o dono — recarrega quando muda.
  const { tipo: tipoDoDono, id: idDoDono } = dono;

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDocumentos(await listarDocumentos({ tipo: tipoDoDono, id: idDoDono } as DonoDoDocumento));
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar os documentos.');
    } finally {
      setCarregando(false);
    }
  }, [tipoDoDono, idDoDono]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const validar = (f: Formulario): string | null => {
    if (!f.nome.trim()) return 'Informe o nome do documento.';
    if (f.emitidoEm && !dataValida(f.emitidoEm)) return MENSAGEM_DATA.invalida;

    if (f.modoValidade === 'data') {
      if (f.validade && !dataValida(f.validade)) return MENSAGEM_DATA.invalida;
      if (f.emitidoEm && f.validade && f.emitidoEm > f.validade) {
        return 'A emissão não pode ser posterior ao vencimento.';
      }
      return null;
    }

    const problema = validarPeriodo(f.validadePeriodo);
    if (problema) return problema;
    // A constraint exige emissão quando a validade é Período: sem o ponto de
    // partida não há de onde contar.
    if (f.validadePeriodo.valor && !f.emitidoEm) {
      return 'Informe a data de emissão para contar a validade a partir dela.';
    }
    return null;
  };

  const salvar = async () => {
    if (!form) return;
    const problema = validar(form);
    if (problema) {
      setErro(problema);
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      let documento = await gravarDocumento({
        id: form.id,
        dono,
        tipo: form.tipo,
        nome: form.nome.trim(),
        numero: form.numero.trim() || null,
        descricao: form.descricao.trim() || null,
        emitidoEm: form.emitidoEm || null,
        validade: form.modoValidade === 'data' ? form.validade || null : null,
        validadePeriodo:
          form.modoValidade === 'periodo' ? form.validadePeriodo : { valor: null, unidade: null },
        profissaoId: form.tipo === 'registro_profissional' ? profissaoId : null,
      });

      if (arquivoEscolhido) {
        documento = await anexarArquivo(documento, arquivoEscolhido);
      }

      setForm(null);
      setArquivoEscolhido(null);
      if (arquivoRef.current) arquivoRef.current.value = '';
      await carregar();
      onMudou?.();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao gravar o documento.');
    } finally {
      setSalvando(false);
    }
  };

  const abrirArquivo = async (documento: Documento) => {
    if (!documento.arquivoCaminho) return;
    const url = await urlDoArquivo(documento.arquivoCaminho);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else setErro('Não foi possível abrir o arquivo.');
  };

  const remover = async (documento: Documento) => {
    if (!window.confirm(`Desativar o documento "${documento.nome}"?`)) return;
    try {
      await removerDocumentoLogicamente(documento.id);
      await carregar();
      onMudou?.();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao desativar o documento.');
    }
  };

  const editar = (documento: Documento) => {
    setForm({
      id: documento.id,
      tipo: documento.tipo,
      nome: documento.nome,
      numero: documento.numero ?? '',
      descricao: documento.descricao ?? '',
      emitidoEm: documento.emitidoEm?.slice(0, 10) ?? '',
      modoValidade: documento.validadePeriodo.valor ? 'periodo' : 'data',
      validade: documento.validade?.slice(0, 10) ?? '',
      validadePeriodo: documento.validadePeriodo,
    });
    setErro(null);
  };

  return (
    <section className="rounded-lg border border-background-200/70 bg-background-100/40 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h4 className="text-sm font-semibold text-foreground-900">
          {titulo}
          <span className="ml-2 text-xs font-normal text-foreground-500">{documentos.length}</span>
        </h4>
        {!somenteLeitura && !form && (
          <button
            type="button"
            onClick={() => setForm(formularioVazio(tipos[0]))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-add-line"></i>
            Adicionar documento
          </button>
        )}
      </div>

      {erro && (
        <p role="alert" className="text-xs text-red-600 mb-3">
          {erro}
        </p>
      )}

      {carregando ? (
        <p className="text-xs text-foreground-500">Carregando...</p>
      ) : documentos.length === 0 && !form ? (
        <p className="text-xs text-foreground-500">Nenhum documento anexado.</p>
      ) : (
        <ul className="space-y-2">
          {documentos.map((d) => {
            const situacao = situacaoDoDocumento(d, diasDeAlerta);
            return (
              <li
                key={d.id}
                className="flex items-start justify-between gap-3 flex-wrap rounded-md border border-background-200/60 bg-background-50 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground-900 truncate">
                      {d.nome}
                    </span>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${CLASSE_SITUACAO_DOCUMENTO[situacao]}`}
                    >
                      {ROTULO_SITUACAO_DOCUMENTO[situacao]}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-500 mt-0.5">
                    {ROTULO_TIPO[d.tipo]}
                    {d.numero ? ` · nº ${d.numero}` : ''}
                    {d.emitidoEm ? ` · emitido ${formatData(d.emitidoEm)}` : ''}
                    {d.venceEm ? ` · vence ${formatData(d.venceEm)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {d.arquivoCaminho && (
                    <button
                      type="button"
                      onClick={() => void abrirArquivo(d)}
                      title={d.arquivoNome ?? 'Abrir arquivo'}
                      className="p-1.5 rounded-md text-foreground-600 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-file-download-line"></i>
                      <span className="sr-only">Abrir arquivo de {d.nome}</span>
                    </button>
                  )}
                  {!somenteLeitura && (
                    <>
                      <button
                        type="button"
                        onClick={() => editar(d)}
                        className="p-1.5 rounded-md text-foreground-600 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-edit-line"></i>
                        <span className="sr-only">Editar {d.nome}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => void remover(d)}
                        className="p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <i className="ri-delete-bin-line"></i>
                        <span className="sr-only">Desativar {d.nome}</span>
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {form && (
        <div className="mt-3 pt-3 border-t border-background-200/60 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tipos.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoDeDocumento })}
                  className={CLASSE}
                >
                  {tipos.map((t) => (
                    <option key={t} value={t}>
                      {ROTULO_TIPO[t]}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Nome<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={form.nome}
                maxLength={LIMITE_TEXTO}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className={CLASSE}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Número do documento
              </label>
              <input
                type="text"
                value={form.numero}
                maxLength={LIMITE_TEXTO}
                onChange={(e) => setForm({ ...form, numero: e.target.value })}
                className={CLASSE}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Emissão
              </label>
              <input
                type="date"
                value={form.emitidoEm}
                onChange={(e) => setForm({ ...form, emitidoEm: e.target.value })}
                className={CLASSE}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Como a validade é contada
              </label>
              <select
                value={form.modoValidade}
                onChange={(e) =>
                  setForm({ ...form, modoValidade: e.target.value as 'data' | 'periodo' })
                }
                className={CLASSE}
              >
                <option value="data">Data de vencimento</option>
                <option value="periodo">Prazo a partir da emissão</option>
              </select>
            </div>

            {form.modoValidade === 'data' ? (
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Vencimento
                </label>
                <input
                  type="date"
                  value={form.validade}
                  onChange={(e) => setForm({ ...form, validade: e.target.value })}
                  className={CLASSE}
                />
                <p className="text-xs text-foreground-500 mt-1">
                  Em branco: documento sem vencimento.
                </p>
              </div>
            ) : (
              <CampoPeriodo
                rotulo="Validade"
                valor={form.validadePeriodo}
                onChange={(p) => setForm({ ...form, validadePeriodo: p })}
                ajuda="Contado a partir da emissão."
                textoVazio="documento sem vencimento."
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Observações
            </label>
            <textarea
              value={form.descricao}
              maxLength={LIMITE_TEXTO_LONGO}
              rows={2}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              className={CLASSE}
            />
          </div>

          <div>
            <label
              htmlFor="documento-arquivo"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Arquivo (PDF ou imagem, até 10 MB)
            </label>
            <input
              id="documento-arquivo"
              ref={arquivoRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setArquivoEscolhido(e.target.files?.[0] ?? null)}
              className="text-sm text-foreground-700"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void salvar()}
              disabled={salvando}
              className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium disabled:opacity-60"
            >
              {salvando ? 'Gravando...' : 'Gravar documento'}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(null);
                setArquivoEscolhido(null);
                setErro(null);
              }}
              className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
